import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Award, ArrowDown, ArrowUp, Gift } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';

export default function Loyalty() {
  const { t } = useTranslation('account');
  const addToast = useUIStore((s) => s.addToast);
  const { data: settingsData } = useSettings();
  const config = settingsData?.settings?.upsell_config?.loyalty_points;

  const { data: balanceData, refetch } = useQuery({
    queryKey: ['loyalty-balance'],
    queryFn: () =>
      api.get<{ balance: number }>('/loyalty/balance'),
  });

  const { data: historyData } = useQuery({
    queryKey: ['loyalty-history'],
    queryFn: () =>
      api.get<{ history: any[]; total: number }>('/loyalty/history'),
  });

  const [redeemAmount, setRedeemAmount] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState<{ coupon_code: string; discount_amount: number } | null>(null);

  const balance = balanceData?.balance || 0;
  const history = historyData?.history || [];
  const rate = config?.redemption_rate || 100;
  const value = config?.redemption_value || 1;

  async function handleRedeem(e: React.FormEvent) {
    e.preventDefault();
    const points = parseInt(redeemAmount);
    if (!points || points <= 0 || points > balance) return;

    setIsRedeeming(true);
    try {
      const result = await api.post<{ coupon_code: string; discount_amount: number }>('/loyalty/redeem', { points });
      setRedeemResult(result);
      refetch();
      setRedeemAmount('');
    } catch (err) {
      addToast(err instanceof Error ? err.message : t('loyalty.redeemFailed'), 'error');
    } finally {
      setIsRedeeming(false);
    }
  }

  if (!config?.enabled) {
    return (
      <div className="text-center py-16">
        <Award className="h-16 w-16 text-ink-muted mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-ink">{t('loyalty.unavailableHeading')}</h1>
        <p className="mt-2 text-ink-muted">{t('loyalty.unavailable')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-bold text-ink">{t('loyalty.heading')}</h1>

      {/* Balance card */}
      <div className="bg-gradient-to-r from-sage-700 to-sage-800 rounded-xl p-6 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Award className="h-5 w-5" aria-hidden="true" />
          <span className="text-sm text-sage-200">{t('loyalty.balanceLabel')}</span>
        </div>
        <p className="text-4xl font-bold">{balance.toLocaleString()}</p>
        <p className="text-sm text-sage-200 mt-1">
          {t('loyalty.rewardsValue', { value: formatCurrency((balance / rate) * value) })}
        </p>
      </div>

      {/* How it works */}
      <div className="bg-surface rounded-lg border border-line p-6">
        <h2 className="font-semibold text-ink mb-3">{t('loyalty.howItWorks')}</h2>
        <div className="grid gap-4 sm:grid-cols-3 text-sm">
          <div className="text-center p-3 bg-surface-soft rounded-lg">
            <p className="font-bold text-sage-700 text-lg">{config.points_per_dollar || 10}x</p>
            <p className="text-ink-soft">{t('loyalty.pointsPerDollar')}</p>
          </div>
          <div className="text-center p-3 bg-surface-soft rounded-lg">
            <p className="font-bold text-sage-700 text-lg">{config.points_per_review || 50}</p>
            <p className="text-ink-soft">{t('loyalty.pointsPerReview')}</p>
          </div>
          <div className="text-center p-3 bg-surface-soft rounded-lg">
            <p className="font-bold text-sage-700 text-lg">
              {t('loyalty.redemptionRate', { points: rate })}
            </p>
            <p className="text-ink-soft">
              {t('loyalty.redemptionRateValue', { value: formatCurrency(value) })}
            </p>
          </div>
        </div>
      </div>

      {/* Redeem */}
      {balance > 0 && (
        <div className="bg-surface rounded-lg border border-line p-6">
          <h2 className="font-semibold text-ink mb-3">{t('loyalty.redeemHeading')}</h2>
          {redeemResult ? (
            <div className="text-center p-4 bg-success-50 rounded-lg">
              <Gift className="h-8 w-8 text-success-600 mx-auto mb-2" aria-hidden="true" />
              <p className="font-semibold text-success-800">{t('loyalty.couponCreated')}</p>
              <p className="text-2xl font-bold text-success-900 font-mono mt-1">{redeemResult.coupon_code}</p>
              <p className="text-sm text-success-700 mt-1">
                {t('loyalty.couponDescription', { value: formatCurrency(redeemResult.discount_amount) })}
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setRedeemResult(null)}>
                {t('loyalty.done')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRedeem} className="flex gap-3">
              <input
                type="number"
                value={redeemAmount}
                onChange={(e) => setRedeemAmount(e.target.value)}
                placeholder={t('loyalty.redeemPlaceholder', { max: balance })}
                max={balance}
                min={rate}
                className="flex-1 px-3 py-2 border border-line-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
              />
              <Button type="submit" isLoading={isRedeeming} disabled={!redeemAmount || parseInt(redeemAmount) > balance}>
                {t('loyalty.redeem')}
              </Button>
            </form>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-surface rounded-lg border border-line overflow-hidden">
          <h2 className="font-semibold text-ink px-6 py-4 border-b border-line">{t('loyalty.history')}</h2>
          <div className="divide-y divide-line">
            {history.map((entry: any) => (
              <div key={entry.id} className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                  {entry.points > 0 ? (
                    <ArrowUp className="h-4 w-4 text-success-600" aria-hidden="true" />
                  ) : (
                    <ArrowDown className="h-4 w-4 text-red-500" aria-hidden="true" />
                  )}
                  <div>
                    <p className="text-sm text-ink">{entry.description}</p>
                    <p className="text-xs text-ink-muted">{new Date(entry.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`font-semibold ${entry.points > 0 ? 'text-success-700' : 'text-red-600'}`}>
                  {entry.points > 0 ? '+' : ''}{entry.points}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
